import { GroupCondition } from '@/api/enums';
import { equal, group } from '@/api/helpers';
import { FiltersType } from '@/api/types';
import { CourseEntityEnrollEnum, MaterialType } from '@/client/courses';
import {
  CourseSchedulePeriodUnit,
  PlannerCRUDCourse,
} from '@/client/planner/types';
import { DEFAULT_ENROLLMENT_HOUR } from '@/common/constants';
import { ExcludeFormValue, TargetFormValue } from '@/common/types';
import { useCoursesPartialRequest } from '@/hooks/query';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { AppButton } from '@/ui/buttons';
import { AppCalendarWithEnrollmentTimePicker } from '@/ui/calendar';
import { FlexContainer } from '@/ui/styled-ui';
import { TimelineIconPath } from '@/utils/helpers';
import { updateTimeline } from '@/utils/planner';
import { getTargetEntityIdsByType } from '@/utils/targets';
import { t } from 'i18next';
import moment from 'moment';
import { ScrollPanel } from 'primereact/scrollpanel';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { PlannerCourseAddButton } from './PlannerCourseAddButton';
import { PlannerImageCard } from './PlannerImageCard';
import { PlannerPeriodSelector } from './PlannerPeriodSelector';
import { PlannerSettings } from './PlannerSettings';

const StyledScrollPanel = styled(ScrollPanel)`
  width: 100%;
  height: 300px;
`;

const StyledDiv = styled.div`
  margin: 0 var(--xsmall-padding);
  position: relative;
  width: 160px;

  &.date-header {
    position: relative;
    margin-left: 36px;

    > svg {
      display: inline-block;
      position: absolute;
      left: -42px;
      height: 100%;
      vertical-align: middle;
      width: 8px;

      path {
        fill: var(--gray-dark);
      }

      &.red {
        path {
          fill: var(--red-main);
        }
      }
    }
  }
`;

const StyledDateSpan = styled.span`
  font-size: var(--xsmall-font-size);
  text-align: center;
  color: var(--gray-darker);
  margin: auto;
  z-index: 99;
  position: absolute;
  bottom: -16px;
  left: 0;

  &.red {
    color: var(--red-main);
    left: 3px;
  }
`;

const StyledPlaceholderDiv = styled.div`
  height: 100%;
  min-height: 132px;
  border: 2px dashed var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
`;

const StyledDatePlaceholderDiv = styled.div`
  height: 48px;
  border: 2px dashed var(--gray-dark);
  border-radius: var(--xsmall-border-radius);
`;

type PlannerCRUDTimelineProps = {
  items: PlannerCRUDCourse[];
  targets: TargetFormValue[];
  excludes: ExcludeFormValue[];
  isOnboardingPlan?: boolean;
  onItemAdd: (item: PlannerCRUDCourse) => void;
  onItemDateChange: (
    date: Date,
    item: PlannerCRUDCourse,
    plannerScheduleDate?: Date | null,
  ) => void;
  onItemEnrollmentHourChange: (
    enrollmentHour: number,
    item: PlannerCRUDCourse,
  ) => void;
  onItemEnrollImmediatelyChange: (
    enrollImmediately: boolean,
    item: PlannerCRUDCourse,
  ) => void;
  onItemPeriodChange: (
    periodValue: number,
    periodType: CourseSchedulePeriodUnit,
    itemId: string,
  ) => void;
  onItemSkipUsersWithinGracePeriodToggle: (
    skipUsersWithinGracePeriod: boolean,
    item: PlannerCRUDCourse,
  ) => void;
  onItemRemove: (item: PlannerCRUDCourse) => void;
  additionalFilters?: FiltersType;
};

export const PlannerCRUDTimeline: React.FC<PlannerCRUDTimelineProps> = ({
  items,
  targets,
  excludes,
  isOnboardingPlan,
  onItemAdd,
  onItemDateChange,
  onItemEnrollmentHourChange,
  onItemEnrollImmediatelyChange,
  onItemPeriodChange,
  onItemSkipUsersWithinGracePeriodToggle,
  onItemRemove,
  additionalFilters,
}) => {
  const account = useAppSelector(selectCurrentAccount);
  const scrollPanelRef = useRef<any>(null);
  const latestAddedCourseRef = useRef<HTMLDivElement>(null);
  const [timelineItems, setTimelineItems] = useState<PlannerCRUDCourse[]>([]);
  const [arePastCoursesVisible, setArePastCoursesVisible] = useState(false);
  const [lastAddedCourse, setLastAddedCourse] =
    useState<PlannerCRUDCourse | null>(null);

  const {
    courses,
    isLoading: isCoursesLoading,
    refetch,
  } = useCoursesPartialRequest({
    accountId: account?.id as string,
    includeEnrollmentStatistics: 1,
    targetSelector: targets
      ? {
          targets: {
            branches: getTargetEntityIdsByType(
              targets,
              CourseEntityEnrollEnum.BRANCHES,
            ),
            groups: getTargetEntityIdsByType(
              targets,
              CourseEntityEnrollEnum.GROUPS,
            ),
          },
        }
      : undefined,
    filters: [
      equal('retired', false),
      group(GroupCondition.OR, [
        equal('type', MaterialType.COURSE),
        equal('type', MaterialType.CUSTOM_MATERIAL),
      ]),
      ...(additionalFilters ? additionalFilters : []),
    ],
    sort: [['priority', 'asc'].join(',')],
  });

  useEffect(() => {
    updateTimeline(items, setTimelineItems, isOnboardingPlan);

    const slider = scrollPanelRef?.current?.getElement();
    const draggableElement = slider?.querySelector('.p-scrollpanel-content');
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      draggableElement.scrollBy({ left: event.deltaY < 0 ? -30 : 30 });
    };

    if (draggableElement) {
      draggableElement.addEventListener('wheel', handleWheel);
    }

    return () => {
      if (draggableElement) {
        draggableElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, [items]);

  useEffect(() => {
    if (lastAddedCourse && scrollPanelRef.current) {
      setTimeout(() => {
        const scrollPanel = scrollPanelRef.current.getElement();
        const content = scrollPanel?.querySelector('.p-scrollpanel-content');

        if (content) {
          const courseContainers = content.querySelectorAll('[data-course-id]');
          let targetElement: Element | null = null;

          courseContainers.forEach((container: Element) => {
            const courseId = container.getAttribute('data-course-id');
            const enrollDate = container.getAttribute('data-enroll-date');

            if (
              !isOnboardingPlan &&
              courseId === lastAddedCourse.id &&
              enrollDate === moment(lastAddedCourse.enrollDate).toISOString()
            ) {
              targetElement = container;
            } else if (isOnboardingPlan && courseId === lastAddedCourse.id) {
              targetElement = container;
            }
          });

          if (targetElement) {
            const scrollLeft =
              (targetElement as HTMLElement).offsetLeft -
              content.clientWidth / 2 +
              (targetElement as HTMLElement).clientWidth / 2;

            content.scrollTo({
              left: Math.max(0, scrollLeft),
              behavior: 'smooth',
            });
          }
        }

        setLastAddedCourse(null);
      }, 0);
    }
  }, [lastAddedCourse]);

  useEffect(() => {
    refetch();
  }, [targets]);

  const isAllTimelineItemsPastDate = () => {
    return timelineItems.every((item) => item.isPastDate);
  };

  const Placeholder = () => (
    <div className="flex relative pl-3 pr-3">
      <div>
        <StyledDateSpan className="red">{t('now')}</StyledDateSpan>
        <StyledDiv className="date-header relative">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="204"
            viewBox="0 0 7 204"
            fill="none"
            className="red"
          >
            <TimelineIconPath />
          </svg>
          <StyledPlaceholderDiv />
          <StyledDatePlaceholderDiv className="mt-2" />
          <StyledDatePlaceholderDiv className="mt-2" />
        </StyledDiv>
      </div>
    </div>
  );

  const handleItemAdd = (item: PlannerCRUDCourse) => {
    onItemAdd(item);
    setLastAddedCourse(item);
  };

  return (
    <div className="w-full">
      {items?.length ? (
        <StyledScrollPanel ref={scrollPanelRef}>
          <div className="flex pb-5">
            {!arePastCoursesVisible &&
              timelineItems.some(({ isPastDate }) => isPastDate) && (
                <div className="flex align-items-center mr-3">
                  <AppButton
                    size="xs"
                    icon="pi pi-history"
                    onClick={() => setArePastCoursesVisible(true)}
                    tooltip={t('planner.see.past.content')}
                  />
                </div>
              )}

            {timelineItems
              .filter((item) => !!item)
              .map((item, index) => (
                <div
                  key={`${index}${item.enrollDate}${item.id}`}
                  className="flex relative"
                  data-course-id={item.id}
                  data-enroll-date={moment(item.enrollDate).toISOString()}
                  ref={
                    lastAddedCourse &&
                    item.id === lastAddedCourse.id &&
                    moment(item.enrollDate).toISOString() ===
                      moment(lastAddedCourse.enrollDate).toISOString()
                      ? latestAddedCourseRef
                      : undefined
                  }
                >
                  {(!item.isPastDate ||
                    (item.isPastDate && arePastCoursesVisible)) && (
                    <div className="pl-3 pr-3">
                      {item.isDateHeader && (
                        <StyledDateSpan
                          className={
                            item?.firstNonPastDate && !isOnboardingPlan
                              ? 'red'
                              : ''
                          }
                        >
                          {item.firstNonPastDate && !isOnboardingPlan
                            ? t('now')
                            : item.dateKey}
                        </StyledDateSpan>
                      )}
                      <StyledDiv
                        className={
                          item.isDateHeader
                            ? 'date-header relative'
                            : 'relative'
                        }
                      >
                        {item.isDateHeader && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            height="204"
                            viewBox="0 0 7 204"
                            fill="none"
                            className={
                              item?.firstNonPastDate && !isOnboardingPlan
                                ? 'red'
                                : ''
                            }
                          >
                            <TimelineIconPath />
                          </svg>
                        )}
                        <PlannerImageCard
                          item={item}
                          isOnboardingPlan={isOnboardingPlan}
                          onItemRemove={onItemRemove}
                          isCRUD
                        />
                        {!isOnboardingPlan && (
                          <AppCalendarWithEnrollmentTimePicker
                            id="enroll-date"
                            name="enrollDate"
                            className="w-full white-input mt-2"
                            placeholder={t('generic.select')}
                            value={
                              item?.enrollDate
                                ? moment(item.enrollDate).toDate()
                                : undefined
                            }
                            enrollmentHour={
                              item.enrollmentHour ?? DEFAULT_ENROLLMENT_HOUR
                            }
                            enrollImmediately={item.enrollImmediately ?? false}
                            minDate={
                              moment(item.plannerScheduleDate).isAfter(
                                moment(),
                                'days',
                              )
                                ? moment(item.plannerScheduleDate).toDate()
                                : new Date()
                            }
                            onDateChange={(date: Date) => {
                              onItemDateChange(
                                moment(date).utc(true).toDate(),
                                item,
                              );
                            }}
                            onEnrollmentHourChange={(hour: number) => {
                              onItemEnrollmentHourChange(hour, item);
                            }}
                            onEnrollImmediatelyChange={(
                              immediately: boolean,
                            ) => {
                              onItemEnrollImmediatelyChange(immediately, item);
                            }}
                            disabledDates={timelineItems
                              .filter((i) => i.id === item.id && i.enrollDate)
                              .map((i) => new Date(i?.enrollDate as Date))}
                            readOnlyInput
                            icon="pi pi-calendar"
                            iconPos="left"
                            style={{ width: '160px !important' }}
                            disabled={item.isPastDate || !!item.executedAt}
                          />
                        )}
                        {isOnboardingPlan && (
                          <PlannerPeriodSelector
                            periodValue={item.periodValue}
                            periodUnit={item.periodUnit}
                            onChange={(
                              periodValue: number,
                              periodUnit: CourseSchedulePeriodUnit,
                            ) =>
                              onItemPeriodChange(
                                periodValue,
                                periodUnit,
                                item.id,
                              )
                            }
                          />
                        )}
                        <PlannerSettings
                          key={`${index}${item.enrollDate}${item.id}`}
                          item={item}
                          targets={targets}
                          excludes={excludes}
                          isDisabled={item.isPastDate || !!item.executedAt}
                          scrollPanelRef={scrollPanelRef}
                          onItemSkipUsersWithinGracePeriodToggle={
                            onItemSkipUsersWithinGracePeriodToggle
                          }
                          isOnboardingPlan={isOnboardingPlan}
                        />
                      </StyledDiv>
                    </div>
                  )}

                  {item.isLastOfDateGroup &&
                    (!item.isPastDate ||
                      isOnboardingPlan ||
                      (index === timelineItems?.length - 1 &&
                        isAllTimelineItemsPastDate())) && (
                      <>
                        {isAllTimelineItemsPastDate() && Placeholder()}
                        <PlannerCourseAddButton
                          courses={courses}
                          timelineItems={timelineItems}
                          currentTimelineItem={
                            isAllTimelineItemsPastDate() ? undefined : item
                          }
                          onItemAdd={handleItemAdd}
                          isCoursesLoading={isCoursesLoading}
                          isOnboarding={isOnboardingPlan}
                        />
                      </>
                    )}
                </div>
              ))}
          </div>
        </StyledScrollPanel>
      ) : (
        <FlexContainer direction="column" gap={0}>
          <PlannerCourseAddButton
            courses={courses}
            timelineItems={timelineItems}
            onItemAdd={handleItemAdd}
            isCoursesLoading={isCoursesLoading}
            isOnboarding={isOnboardingPlan}
          />
        </FlexContainer>
      )}
    </div>
  );
};
